import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarDays, Loader2, User, LogIn, Search, ShoppingCart, FileText, Send, Eye, Clock, Key, ChevronDown, ChevronRight
} from "lucide-react";

// IST converter
const toIST = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const h = String(ist.getUTCHours()).padStart(2, '0');
  const m = String(ist.getUTCMinutes()).padStart(2, '0');
  const s = String(ist.getUTCSeconds()).padStart(2, '0');
  return `${h}:${m}:${s} IST`;
};

const toISTShort = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return `${String(ist.getUTCHours()).padStart(2, '0')}:${String(ist.getUTCMinutes()).padStart(2, '0')}`;
};

const actionLabels = {
  login: "Logged in",
  search_grade: "Searched grade",
  edit_rate: "Edited rate",
  edit_qty: "Edited qty",
  delete_grade: "Deleted grade",
  submit_request: "Submitted order request",
  view_orders: "Opened Orders tab",
  view_ledger: "Opened Ledger tab",
  view_requests: "Opened Requests tab",
  view_profile: "Opened Profile",
  view_invoice: "Viewed invoice breakdown",
  view_dispatch: "Viewed dispatch details",
  click_freq_grade: "Selected frequent grade",
  change_password: "Changed password",
  change_username: "Changed username",
  click_order_row: "Clicked order row",
  view_new_order: "Opened New Order tab",
  click_refresh: "Refreshed data",
};

const actionColors = {
  login: "text-green-700 bg-green-50",
  search_grade: "text-cyan-700 bg-cyan-50",
  edit_rate: "text-orange-700 bg-orange-50",
  edit_qty: "text-orange-700 bg-orange-50",
  delete_grade: "text-red-700 bg-red-50",
  submit_request: "text-emerald-700 bg-emerald-50",
  view_orders: "text-blue-700 bg-blue-50",
  view_ledger: "text-purple-700 bg-purple-50",
  view_requests: "text-amber-700 bg-amber-50",
  view_profile: "text-gray-700 bg-gray-100",
  view_invoice: "text-blue-700 bg-blue-50",
  view_dispatch: "text-emerald-700 bg-emerald-50",
  click_freq_grade: "text-teal-700 bg-teal-50",
  change_password: "text-red-700 bg-red-50",
  change_username: "text-red-700 bg-red-50",
  click_order_row: "text-blue-700 bg-blue-50",
  view_new_order: "text-green-700 bg-green-50",
  click_refresh: "text-gray-600 bg-gray-50",
};

export default function CustomerActivityLog({ filterCustomerName }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [detailPopup, setDetailPopup] = useState(null);
  const [timelinePopup, setTimelinePopup] = useState(null);

  const fetchActivities = async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/activity-log?date=${d}`);
      let data = res.data || [];
      // Filter by customer name if provided
      if (filterCustomerName) {
        data = data.filter(c => c.customer_name === filterCustomerName);
      }
      setActivities(data);
    } catch (e) {
      toast.error("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (date) fetchActivities(date);
  }, [date]);

  const getDetailItems = (customer, actionTypes) => {
    return (customer.activities || []).filter(a => actionTypes.includes(a.action));
  };

  const openDetail = (customer, type) => {
    let items = [];
    let title = "";
    if (type === "login") {
      items = getDetailItems(customer, ["login"]);
      title = "Login Activity";
    } else if (type === "grades") {
      items = getDetailItems(customer, ["search_grade", "click_freq_grade"]);
      title = "Grades Activity";
    } else if (type === "rates_qty") {
      items = getDetailItems(customer, ["edit_rate", "edit_qty", "delete_grade"]);
      title = "Rates & Qty Edits";
    } else if (type === "requests") {
      items = getDetailItems(customer, ["submit_request"]);
      title = "Order Requests";
    } else if (type === "orders_viewed") {
      items = getDetailItems(customer, ["view_orders", "view_invoice", "view_dispatch", "click_order_row"]);
      title = "Orders Activity";
    } else if (type === "ledger_viewed") {
      items = getDetailItems(customer, ["view_ledger"]);
      title = "Ledger Activity";
    } else if (type === "requests_viewed") {
      items = getDetailItems(customer, ["view_requests"]);
      title = "Requests Activity";
    } else if (type === "profile") {
      items = getDetailItems(customer, ["view_profile", "change_password", "change_username"]);
      title = "Profile Activity";
    }
    setDetailPopup({ customer: customer.customer_name, title, items });
  };

  // Build session timeline from activities
  const buildTimeline = (customer) => {
    const acts = [...(customer.activities || [])].reverse(); // chronological
    const sessions = [];
    let currentSession = null;
    for (const a of acts) {
      if (a.action === 'login') {
        if (currentSession) sessions.push(currentSession);
        currentSession = { login_time: a.timestamp, actions: [] };
      }
      if (currentSession) {
        currentSession.actions.push(a);
      }
    }
    if (currentSession) sessions.push(currentSession);
    // Reverse so latest session is first
    sessions.reverse();
    return sessions;
  };

  // Count specific activity types
  const countActions = (customer, types) => {
    return (customer.activities || []).filter(a => types.includes(a.action)).length;
  };

  return (
    <div data-testid="customer-activity-log">
      <div className="flex items-center gap-3 mb-4">
        <CalendarDays className="w-4 h-4 text-muted-foreground" />
        <Input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="h-8 w-40 text-xs" data-testid="activity-date-picker" />
        <span className="text-xs text-muted-foreground">
          {activities.length} customer{activities.length !== 1 ? 's' : ''} active
        </span>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {activities.length > 0 ? (
        <div className="border rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold w-8">#</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold">Customer</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Login</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold">Grades</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold">Rates/Qty</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold">Requests</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Orders</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Ledger</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Requests</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Profile/Pwd</TableHead>
                  <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">All Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activities.map((c, idx) => {
                  const lastAct = c.activities?.[0];
                  const loginTime = c.login_time ? toISTShort(c.login_time) : '-';
                  const rqEdits = c.rates_qty_edits || [];
                  const orderClicks = countActions(c, ["view_orders", "view_invoice", "view_dispatch", "click_order_row"]);
                  const ledgerClicks = countActions(c, ["view_ledger"]);
                  const requestClicks = countActions(c, ["view_requests"]);
                  const profileClicks = countActions(c, ["view_profile", "change_password", "change_username"]);
                  const pwdChanged = (c.activities || []).some(a => a.action === 'change_password' || a.action === 'change_username');
                  const totalActions = (c.activities || []).length;

                  return (
                    <TableRow key={c.customer_name} className="hover:bg-gray-50/50" data-testid={`activity-row-${idx}`}>
                      <TableCell className="text-[10px] py-2 px-2 text-muted-foreground font-medium">{idx + 1}</TableCell>
                      <TableCell className="py-2 px-2">
                        <span className="text-xs font-semibold block">{c.customer_name}</span>
                        {c.username && <span className="text-[9px] text-muted-foreground">@{c.username}</span>}
                      </TableCell>

                      {/* Login — clickable, shows timeline */}
                      <TableCell className="py-2 px-2 text-center">
                        {c.login_count > 0 ? (
                          <button onClick={() => setTimelinePopup(c)} className="hover:opacity-70 transition-opacity">
                            <Badge className="bg-green-100 text-green-800 text-[9px] cursor-pointer">{loginTime}</Badge>
                            {c.login_count > 1 && <span className="text-[8px] text-muted-foreground block">x{c.login_count}</span>}
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Grades */}
                      <TableCell className="py-2 px-2">
                        {c.grades_searched.length > 0 ? (
                          <button onClick={() => openDetail(c, "grades")} className="flex flex-wrap gap-0.5 hover:opacity-70">
                            {c.grades_searched.slice(0, 4).map(g => (
                              <Badge key={g} variant="outline" className="text-[9px] px-1.5 py-0 cursor-pointer">{g}</Badge>
                            ))}
                            {c.grades_searched.length > 4 && <Badge variant="outline" className="text-[9px] px-1 py-0">+{c.grades_searched.length - 4}</Badge>}
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Rates/Qty */}
                      <TableCell className="py-2 px-2">
                        {rqEdits.length > 0 ? (
                          <button onClick={() => openDetail(c, "rates_qty")} className="hover:opacity-70">
                            <Badge className="bg-cyan-100 text-cyan-800 text-[9px] cursor-pointer">{rqEdits.length} edit{rqEdits.length > 1 ? 's' : ''}</Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Requests submitted */}
                      <TableCell className="py-2 px-2">
                        {c.order_requests_submitted > 0 ? (
                          <button onClick={() => openDetail(c, "requests")} className="hover:opacity-70">
                            <Badge className="bg-emerald-100 text-emerald-800 text-[9px] cursor-pointer">{c.order_requests_submitted} req</Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Orders viewed — count of all order-related clicks */}
                      <TableCell className="py-2 px-2 text-center">
                        {orderClicks > 0 ? (
                          <button onClick={() => openDetail(c, "orders_viewed")} className="hover:opacity-70">
                            <Badge className="bg-blue-100 text-blue-800 text-[9px] cursor-pointer">{orderClicks}x</Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Ledger viewed */}
                      <TableCell className="py-2 px-2 text-center">
                        {ledgerClicks > 0 ? (
                          <button onClick={() => openDetail(c, "ledger_viewed")} className="hover:opacity-70">
                            <Badge className="bg-purple-100 text-purple-800 text-[9px] cursor-pointer">{ledgerClicks}x</Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Requests viewed */}
                      <TableCell className="py-2 px-2 text-center">
                        {requestClicks > 0 ? (
                          <button onClick={() => openDetail(c, "requests_viewed")} className="hover:opacity-70">
                            <Badge className="bg-amber-100 text-amber-800 text-[9px] cursor-pointer">{requestClicks}x</Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* Profile / Password changed */}
                      <TableCell className="py-2 px-2 text-center">
                        {profileClicks > 0 ? (
                          <button onClick={() => openDetail(c, "profile")} className="hover:opacity-70">
                            <Badge className={`text-[9px] cursor-pointer ${pwdChanged ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                              {pwdChanged ? <><Key className="w-2.5 h-2.5 inline mr-0.5" />Changed</> : `${profileClicks}x`}
                            </Badge>
                          </button>
                        ) : <span className="text-[9px] text-muted-foreground">-</span>}
                      </TableCell>

                      {/* All actions — total count, shows full timeline */}
                      <TableCell className="py-2 px-2 text-center">
                        <button onClick={() => setTimelinePopup(c)} className="hover:opacity-70">
                          <Badge className="bg-gray-200 text-gray-800 text-[9px] cursor-pointer font-bold">{totalActions}</Badge>
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground bg-white border rounded-lg">
          <User className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm">{loading ? 'Loading...' : 'No customer activity on this date'}</p>
        </div>
      )}

      {/* Detail Popup — shows filtered activities */}
      <Dialog open={!!detailPopup} onOpenChange={(open) => { if (!open) setDetailPopup(null); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto" data-testid="activity-detail-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope' }}>
              <Clock className="w-4 h-4 text-blue-600" />
              {detailPopup?.title}
            </DialogTitle>
            <DialogDescription className="text-xs">{detailPopup?.customer} — {date}</DialogDescription>
          </DialogHeader>
          {detailPopup?.items?.length > 0 ? (
            <div className="border rounded-lg overflow-hidden mt-2">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-[10px] py-1.5 px-2 font-extrabold w-8">#</TableHead>
                    <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Time (IST)</TableHead>
                    <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Action</TableHead>
                    <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailPopup.items.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-[10px] py-1.5 px-2 text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-[10px] py-1.5 px-2 font-mono whitespace-nowrap font-semibold">
                        {toIST(item.timestamp)}
                      </TableCell>
                      <TableCell className="text-[10px] py-1.5 px-2">
                        <Badge className={`text-[9px] ${actionColors[item.action] || 'bg-gray-50 text-gray-700'}`}>
                          {actionLabels[item.action] || item.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[10px] py-1.5 px-2 font-medium">
                        {item.details || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No details available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Timeline Popup — full session view with all actions */}
      <Dialog open={!!timelinePopup} onOpenChange={(open) => { if (!open) setTimelinePopup(null); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto" data-testid="timeline-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope' }}>
              <User className="w-4 h-4 text-blue-600" />
              {timelinePopup?.customer_name} — Full Activity Timeline
            </DialogTitle>
            <DialogDescription className="text-xs">
              {date} | {(timelinePopup?.activities || []).length} total actions
            </DialogDescription>
          </DialogHeader>
          {timelinePopup && (() => {
            const sessions = buildTimeline(timelinePopup);
            return (
              <div className="space-y-4 mt-2">
                {sessions.map((session, si) => {
                  const isLatest = si === 0;
                  return (
                    <details key={si} open={isLatest} className="border rounded-lg overflow-hidden group">
                      <summary className="bg-green-50 px-3 py-1.5 border-b flex items-center gap-2 cursor-pointer list-none select-none hover:bg-green-100 transition-colors">
                        <ChevronRight className="w-3 h-3 text-green-600 transition-transform group-open:rotate-90" />
                        <LogIn className="w-3 h-3 text-green-600" />
                        <span className="text-[10px] font-bold text-green-800">
                          {isLatest ? 'Latest Session' : `Session ${sessions.length - si}`} — Login at {toIST(session.login_time)}
                        </span>
                        <Badge variant="outline" className="text-[9px] ml-auto">{session.actions.length} actions</Badge>
                      </summary>
                      <div className="divide-y">
                        {session.actions.map((a, ai) => (
                        <div key={ai} className="px-3 py-1.5 flex items-start gap-2 text-[10px]">
                          <span className="font-mono text-muted-foreground whitespace-nowrap w-16 flex-shrink-0 pt-0.5">
                            {toISTShort(a.timestamp)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <Badge className={`text-[9px] ${actionColors[a.action] || 'bg-gray-50 text-gray-700'}`}>
                              {actionLabels[a.action] || a.action}
                            </Badge>
                            {a.details && <span className="ml-1.5 text-gray-700 font-medium">{a.details}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                  );
                })}
                {sessions.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No sessions found</p>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
